import { useCallback, useEffect, useState } from 'react';

import { PRODUCT_CATEGORY_URL_PARAM } from '~constants';
import { useProductCategories, useProductCategoryResolver } from '~hooks';
import { Product, ProductCategory } from '~types/appsync.types';
import type { ProductCategoryOption } from '~types/productCategory';
import { parseProductCategoryFromUrl, updateUrlParam } from '~utils';

export interface UseProductCategorySelectorOptions {
  /** Pre-set category shortname (from HTML attribute). Hides the category dropdown when set. */
  initialCategoryShortname?: string;
  /** Pre-set contractor. Filters product categories by contractor when set. */
  contractorId?: number;
  /** Sync the selected category to/from the `prodcat` URL param. Default: true. */
  syncUrl?: boolean;
  /** Skip all GraphQL queries (use when an external resolver already provides data). */
  skipQueries?: boolean;
}

export interface UseProductCategorySelectorResult {
  selectedProductCategoryShortname: string;
  handleProductCategoryChange: (
    shortname: string,
    options?: { preserveSelections?: boolean },
  ) => void;
  hasPredefinedProductCategory: boolean;
  productCategory: ProductCategory | undefined;
  products: Product[] | undefined;
  loadingProductCategory: boolean;
  productCategories: ProductCategoryOption[];
  loadingProductCategories: boolean;
  productCategoriesError: boolean;
  productCategoryError: boolean;
}

/**
 * useProductCategorySelector — manages product category selection state, URL sync, and data fetching.
 *
 * Follows the same pattern as `useDateSelector` / `useParticipantSelector`:
 * - Initialises state from URL when `syncUrl` is true
 * - Writes changes back to the URL via `updateUrlParam`
 * - Fetches the category list and resolves the selected product category
 *
 * When `skipQueries` is true no GraphQL calls are made (useful when the caller
 * already provides resolved data, e.g. from a parent widget).
 */
const useProductCategorySelector = ({
  initialCategoryShortname = '',
  contractorId,
  syncUrl = true,
  skipQueries = false,
}: UseProductCategorySelectorOptions = {}): UseProductCategorySelectorResult => {
  const hasPredefinedProductCategory = !!initialCategoryShortname;

  // Init state: predefined > URL > empty
  const [selectedProductCategoryShortname, setSelectedProductCategoryShortname] = useState(() => {
    if (initialCategoryShortname) return initialCategoryShortname;
    if (!syncUrl) return '';
    const searchParams = new URL(globalThis.location.href).searchParams;
    return parseProductCategoryFromUrl(searchParams) ?? '';
  });

  // --- Category list query (dropdown options) ---
  const [productCategories, loadingProductCategories, productCategoriesError] =
    useProductCategories(contractorId, {
      skip: hasPredefinedProductCategory || skipQueries,
    });

  // --- Resolve selected product category ---
  const {
    productCategory,
    products,
    loading: loadingProductCategory,
    productCategoryError,
  } = useProductCategoryResolver({
    productCategoryShortname: skipQueries ? undefined : selectedProductCategoryShortname,
    propagateErrors: false,
  });

  // Sync selected category to URL
  useEffect(() => {
    if (!syncUrl || hasPredefinedProductCategory) return;
    updateUrlParam(PRODUCT_CATEGORY_URL_PARAM, selectedProductCategoryShortname || undefined);
  }, [selectedProductCategoryShortname, syncUrl, hasPredefinedProductCategory]);

  const handleProductCategoryChange = useCallback(
    (shortname: string, _options?: { preserveSelections?: boolean }) => {
      setSelectedProductCategoryShortname(shortname);
    },
    [],
  );

  return {
    handleProductCategoryChange,
    hasPredefinedProductCategory,
    loadingProductCategories,
    loadingProductCategory,
    productCategories,
    productCategoriesError: !!productCategoriesError,
    productCategory,
    productCategoryError: !!productCategoryError,
    products,
    selectedProductCategoryShortname,
  };
};

export default useProductCategorySelector;
